<script setup lang="ts">
import clsx from 'clsx'

import modal from '@/services/modal'

import IconActionConfig from '@/components/atoms/icons/IconActionConfig.vue'
import IconActionDelete from '@/components/atoms/icons/IconActionDelete.vue'
import IconActionToDown from '@/components/atoms/icons/IconActionToDown.vue'
import IconActionToUp from '@/components/atoms/icons/IconActionToUp.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'

const props = defineProps<{
    section: any
    sectionIndex: number
    hasConfig: boolean
    hasDelete: boolean
    isActive: boolean
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()

const isFirstSection = (sectionIndex: number) => {
    let indexCompare = 0

    if (editorPageStore.activePage.content[0].component?.includes('home')) {
        indexCompare = 1
    }

    return sectionIndex === indexCompare
}

const isLastSection = (sectionIndex: number) => {
    return sectionIndex === editorPageStore.activePage.content.length - 1
}

const onSelectSection = () => {
    betaStore.setSelectedSection(props.section, props.sectionIndex)
    betaStore.clearSelectedElement()
    betaStore.clearEditingElement()
    betaStore.setElementForEdit(null)
    betaStore.setSidebarReference(props.hasConfig ? 'section' : null)
}

const onDeleteSection = async () => {
    const result = await modal.confirmation('Excluir seção', 'Deseja realmente excluir a seção da página?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    try {
        await betaStore.deleteSectionAtIndex(props.sectionIndex)
    } catch (error) {
        console.warn(error)
    }
}
</script>

<template>
    <div :class="clsx('editor-beta-section-rail', props.isActive && 'is-active')">
        <button
            v-if="props.hasConfig"
            type="button"
            class="editor-beta-section-rail__button"
            @click="onSelectSection"
        >
            <span class="sr-only">Configurar seção</span>
            <icon-action-config class="h-[18px] w-[18px]" />
        </button>

        <button
            type="button"
            class="editor-beta-section-rail__button"
            :disabled="isFirstSection(props.sectionIndex)"
            @click="betaStore.moveSection(props.sectionIndex, 'up')"
        >
            <span class="sr-only">Mover seção para cima</span>
            <icon-action-to-up class="h-[18px] w-[18px]" />
        </button>

        <button
            type="button"
            class="editor-beta-section-rail__button"
            :disabled="isLastSection(props.sectionIndex)"
            @click="betaStore.moveSection(props.sectionIndex, 'down')"
        >
            <span class="sr-only">Mover seção para baixo</span>
            <icon-action-to-down class="h-[18px] w-[18px]" />
        </button>

        <button
            v-if="props.hasDelete"
            type="button"
            class="editor-beta-section-rail__button is-danger"
            @click="onDeleteSection"
        >
            <span class="sr-only">Excluir seção</span>
            <icon-action-delete class="h-[18px] w-[18px]" />
        </button>
    </div>
</template>

<style scoped>
.editor-beta-section-rail {
    @apply absolute right-[-56px] top-0 z-30 flex flex-col items-center gap-1 rounded-[18px] border border-[#E4E8F1] bg-white p-1.5 opacity-0 shadow-[0_12px_28px_rgba(15,23,42,0.1)] transition-all duration-200;
    pointer-events: none;
}

.editor-beta-section-rail.is-active {
    @apply opacity-100;
    pointer-events: auto;
}

.editor-beta-section-rail__button {
    @apply flex h-9 w-9 items-center justify-center rounded-xl text-[#51657F] transition-all duration-200 hover:bg-[#F4F7FB] disabled:cursor-not-allowed disabled:text-[#B7C3D5];
}

.editor-beta-section-rail__button :deep(svg) {
    @apply stroke-current;
}

.editor-beta-section-rail__button.is-danger {
    @apply text-[#D0657B];
}
</style>
